import React, { useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { mapValues, isEmpty } from 'lodash-es';

import ChartLegend from '../../ChartLegend';
import Skeleton from '../../../../../components/Skeleton';
import { SmartChart } from '../../../../../components/Chart';
import useChartLegend, {
  useToggleSeriesVisibility,
  useHoveredItemClassName,
} from '../../../hooks/useChartLegend';
import { marketShareConfig, colors } from './config';
import { basePartialFilters } from '../../../configs/chart-config';

const emptyObject = {};
let timeoutId;

const SharesChart = ({ metricSeries, chartType, apps, isPending, isCumulative, isPress }) => {
  const chartRef = useRef(null);

  const [isChartRendered, setChartRendered] = useState(false);

  const appsRefNos = useMemo(() => apps.map((app) => app.unified_ref_no), [apps]);
  const { activeItem, hoveredItem, onClick, onMouseOver, onMouseOut } = useChartLegend();
  const hoveredItemClassName = useHoveredItemClassName(metricSeries, hoveredItem, appsRefNos);
  useToggleSeriesVisibility(chartRef, activeItem);

  const adjustedChartType = isCumulative ? 'spline' : chartType;

  const sortedMetric = useMemo(() => {
    if (isEmpty(metricSeries)) return emptyObject;

    return apps.reduce(
      (acc, app) => ({
        ...acc,
        [app.unified_ref_no]: metricSeries[app.unified_ref_no] || [],
      }),
      {}
    );
  }, [apps, metricSeries]);

  const metaSeriesData = useMemo(() => {
    const appByRefNo = apps.reduce(
      (acc, app, index) => ({
        ...acc,
        [app.unified_ref_no]: { ...app, color: colors[index] },
      }),
      {}
    );

    return mapValues(sortedMetric, (_, appRefNo) => ({
      type: adjustedChartType,
      color: appByRefNo[appRefNo]?.color,
      name: `${appByRefNo[appRefNo]?.nickname || 'Unknown'}`,
      id: appRefNo,
      // manage visible via useToggleSeriesVisibility to preserve built-in animation
    }));
  }, [sortedMetric, apps, adjustedChartType]);

  const chartConfig = useMemo(() => {
    // Workaround: prevent chart's events until the chart animations are finished.
    const toggleChartMask = () => {
      setChartRendered(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setChartRendered(false), 1000); // Duration should be >= chart animation duration
    };

    return marketShareConfig({
      chartType: adjustedChartType,
      unit: isCumulative ? 'pp' : '%',
      activeItem,
      onClick,
      onMouseOver,
      onMouseOut,
      toggleChartMask,
      isPress,
    });
  }, [adjustedChartType, isCumulative, activeItem, onClick, onMouseOver, onMouseOut, isPress]);

  return (
    <div className={cx('shares-chart', hoveredItemClassName)}>
      {isCumulative && (
        <p className="chart-capture">
          Each app starts at 0 at the beginning of the selected period. A rising line means gaining
          share; <br /> falling means losing it. The steeper the slope, the faster the shift.
        </p>
      )}
      <div className={cx('chart-container', { 'pointer-events-none': isChartRendered })}>
        {isPending ? (
          <Skeleton height={340} className="d-flex" />
        ) : (
          <>
            <div onMouseOut={onMouseOut} onBlur={onMouseOut}>
              <SmartChart
                ref={chartRef}
                chartId="market-share-chart"
                series={sortedMetric}
                metaSeriesData={metaSeriesData}
                config={chartConfig}
                allowPartialPeriods
                filters={basePartialFilters}
              />
            </div>
            <ChartLegend
              series={Object.values(metaSeriesData)}
              activeItem={activeItem?.id || hoveredItem?.id}
              onClick={onClick}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
            />
          </>
        )}
      </div>
    </div>
  );
};

SharesChart.propTypes = {
  metricSeries: T.object.isRequired,
  chartType: T.string.isRequired,
  apps: T.array.isRequired,
  isPending: T.bool.isRequired,
  isCumulative: T.bool.isRequired,
  isPress: T.bool,
};

export default SharesChart;
